import React, { useMemo, useRef, useEffect, useState } from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { useQuery } from 'react-query';
import { IResponse } from '../../../../models';
import { IBuildPhasePhoto, IFlattenedPhotoItem } from '../../../../models/phases-of-contruction';
import { getBuildPhasePhotos } from '../../../../fetch';
import { useBuilds } from '../../context';
import { CustomPrevArrow, CustomNextArrow } from './arrows';
import { PhotoItem } from './photo-item';
import { BuildPhotoModal } from './build-photo-modal';
import { SkeletonLoader } from '../../../../components';

export const BuildPhotosCarousel: React.FC = () => {
  const sliderRef = useRef<Slider>(null);
  const { selectedBuild, isLoading: isLoadingBuilds } = useBuilds();
  const [selectedPhoto, setSelectedPhoto] = useState<IFlattenedPhotoItem | null>(null);

  // Use MUI breakpoints for more reliable responsive detection
  const isMobile = useMediaQuery('(max-width:480px)');
  const isTablet = useMediaQuery('(max-width:768px)');
  const isDesktopSmall = useMediaQuery('(max-width:1024px)');

  const { data: photosData, isLoading: isLoadingPhotos } = useQuery<
    IResponse<IBuildPhasePhoto>,
    Error
  >(
    ['build-phase-photos', selectedBuild?.buildId],
    async () => await getBuildPhasePhotos(selectedBuild?.buildId),
    { enabled: !!selectedBuild?.buildId }
  );

  // Flatten photoFiles and add comments only to the first photo in each group
  const photoRecords: IFlattenedPhotoItem[] = useMemo(() => {
    const records = photosData?.records;
    if (!Array.isArray(records)) return [];

    const flattened: IFlattenedPhotoItem[] = [];

    records.forEach(photoRecord => {
      if (Array.isArray(photoRecord.photoFiles)) {
        photoRecord.photoFiles.forEach((photoFile, index) => {
          flattened.push({
            ...photoFile,
            // Only add comment to the first photo in each group
            comment: index === 0 ? photoRecord.comment : undefined
          });
        });
      }
    });

    return flattened;
  }, [photosData]);

  const numPhotos = photoRecords.length;
  const isLoading = isLoadingBuilds || isLoadingPhotos;

  // Calculate slides to show based on current breakpoint
  const slidesToShow = useMemo(() => {
    if (isMobile) return 1;
    if (isTablet) return 2;
    if (isDesktopSmall) return 3;
    return 4;
  }, [isMobile, isTablet, isDesktopSmall]);

  const settings = useMemo(
    () => ({
      dots: false,
      infinite: true,
      speed: 500,
      slidesToShow,
      slidesToScroll: 1,
      centerMode: numPhotos <= slidesToShow,
      centerPadding: isMobile ? '20px' : '0px',
      prevArrow: <CustomPrevArrow />,
      nextArrow: <CustomNextArrow />
      // Remove responsive settings since we're handling it manually
    }),
    [slidesToShow, numPhotos, isMobile]
  );

  // Force slider refresh when breakpoint changes
  useEffect(() => {
    if (sliderRef.current) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        if (sliderRef.current) {
          sliderRef.current.slickGoTo(0);
        }
      }, 50);
    }
  }, [slidesToShow]);

  // Create a key that forces re-render when breakpoints change
  const sliderKey = `slider-${slidesToShow}-${numPhotos}`;

  const handlePhotoClick = (photo: IFlattenedPhotoItem) => {
    setSelectedPhoto(photo);
  };

  const handleCloseModal = () => {
    setSelectedPhoto(null);
  };

  return (
    <Box sx={{ py: 2, px: { xs: 2, md: 4 }, mb: 2 }}>
      <Box sx={{ textAlign: 'left', mb: 4 }}>
        <Typography variant="h3" sx={{ mb: 1 }}>
          {selectedBuild ? (
            `${selectedBuild.neighborhoodName} Lot ${selectedBuild.lotNumber}`
          ) : (
            <SkeletonLoader width="300px" height="2.5rem" />
          )}
        </Typography>
        <Typography variant="h4" sx={{ fontStyle: 'italic', fontWeight: 500 }}>
          {selectedBuild ? (
            `YOUR HOME IS CURRENTLY IN PHASE: ${selectedBuild.status?.toUpperCase()}`
          ) : (
            <SkeletonLoader width="400px" height="2rem" />
          )}
        </Typography>
      </Box>

      <Box
        sx={{
          position: 'relative',
          maxWidth: '100%',
          mx: 'auto',
          pb: 8,
          '& .slick-slide': { px: 1 }
        }}
      >
        {isLoading ? (
          <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
            <Typography variant="body1">Loading photos...</Typography>
          </Box>
        ) : numPhotos > 0 ? (
          <Slider key={sliderKey} ref={sliderRef} {...settings}>
            {photoRecords.map(photo => (
              <PhotoItem
                key={photo.buildCommunicationPhotoFileId}
                photo={photo}
                onPhotoClick={handlePhotoClick}
              />
            ))}
          </Slider>
        ) : (
          <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
            <Typography variant="body1">No photos available yet.</Typography>
          </Box>
        )}
      </Box>

      <BuildPhotoModal selectedPhoto={selectedPhoto} onClose={handleCloseModal} />
    </Box>
  );
};
